import { ReactNode } from "react";
// 组件
import SimpleForm, { SimpleFormItemProps } from "../../SimpleForm";
// 方法
import { dataToUpload, dataToUploads, getFormItemName, getInitialValue, getSelectTableNames } from "../util";
// 常量
import { EdiTableColumnsType, GROUP_NAME } from "../interface";
import { FormInstance } from "antd/lib/form";
import { apiTriggerType } from "../../SuperCascader/interface";

function getFormItem(
  record: any,
  rowKey: number,
  tableElement: HTMLElement | null,
  form?: FormInstance<any>,
  props?: EdiTableColumnsType
): ReactNode {
  if (!props) return null;
  const {
    formItemType,
    name,
    rules,
    style,
    initialValue,
    disabled,
    disabledRender,
    disabledRenderKey,
    required,
    enumCode,
    rowIndex,
    options,
    associationName,
    relativePathName,
    selectFirstValue,
    beforeFormItemFlex,
    afterFormItemFlex,
    middleText,
    emptyAfterValue,
    beforeFormItemWidth,
    beforeFormItemProps,
    afterFormItemProps,
    inputProps,
    textAreaProps,
    inputNumberProps,
    switchProps,
    checkboxProps,
    cascaderProps,
    checkboxGroupProps,
    datePickerProps,
    rangePickerProps,
    superAutoCompleteProps,
    timePickerProps,
    superSelectProps,
    superSelectTableProps,
    dropdownTableProps,
    dropdownSyncTableProps,
    uploadProps,
    uploadImageProps,
    uploadImagesProps,
    formatRules,
  } = props;
  const relativePath = relativePathName
    ? record[relativePathName]
    : initialValue;
  const baseSearchFormProps: SimpleFormItemProps = {
    // @ts-ignore
    name: getFormItemName(rowKey, name),
    initialValue: getInitialValue(initialValue, record, name),
    rules: formatRules ? formatRules({...record, ...(form?.getFieldValue([GROUP_NAME, rowIndex]) ?? {})}) : rules,
    form,
  };
  if (disabledRenderKey && !disabledRender) {
    baseSearchFormProps.disabledRender = record[disabledRenderKey];
  }
  if (required && !baseSearchFormProps.rules) {
    baseSearchFormProps.rules = [
      {
        required,
      },
    ];
  }
  const baseFormProps = { disabled: typeof disabled === 'function' ? disabled({...record, ...(form?.getFieldValue([GROUP_NAME, rowIndex]) ?? {})}) : disabled, };
  const associationFiledName = associationName ? [GROUP_NAME, rowKey, associationName] : undefined;
  switch (formItemType) {
    case 'Input':
      return (
        <SimpleForm.Input
          {...baseSearchFormProps}
          inputProps={{
            ...baseFormProps,
            ...inputProps,
            onChange: (value) => {
              inputProps?.onChange?.(value, {...record, ...(form?.getFieldValue([GROUP_NAME, rowIndex]) ?? {})}, form);
            }
          }}
        />
      );
    case 'TextArea':
      return (
        <SimpleForm.TextArea
          {...baseSearchFormProps}
          textAreaProps={{
            ...baseFormProps,
            ...textAreaProps,
          }}
        />
      );
    case 'SuperAutoComplete':
      return (
        <SimpleForm.SuperAutoComplete
          {...baseSearchFormProps}
          labelValue={(Array.isArray(name) && name.length === 2) ? record?.[name[1]] : undefined}
          name={getSelectTableNames(baseSearchFormProps.name)}
          superAutoCompleteProps={{
            ...baseFormProps,
            ...superAutoCompleteProps,
            beforeFetch: superAutoCompleteProps?.beforeFetch ? (params: any) => {
              return superAutoCompleteProps?.beforeFetch?.(params, (typeof rowIndex === 'number') ? {...record, ...(form?.getFieldValue([GROUP_NAME, rowIndex]) ?? {})} : record, rowIndex)
            } : undefined,
            onChange: (value: any, item: any) => {
              superAutoCompleteProps?.onChange?.(value, item, form, rowIndex, (typeof rowIndex === 'number') ? {...record, ...(form?.getFieldValue([GROUP_NAME, rowIndex]) ?? {})} : record);
            },
          }}
        />
      );
    case 'InputNumber':
      return (
        <SimpleForm.InputNumber
          {...baseSearchFormProps}
          inputNumberProps={{
            ...baseFormProps,
            ...inputNumberProps,
            onChange: (value) => {
              inputNumberProps?.onChange?.(value, {...record, ...(form?.getFieldValue([GROUP_NAME, rowIndex]) ?? {})}, form);
            }
          }}
        />
      );
    case 'DropdownTable':
      return (
        <SimpleForm.DropdownTable
          {...baseSearchFormProps}
          dropdownTableProps={{ ...baseFormProps, ...dropdownTableProps }}
        />
      );
    case 'DropdownSyncTable':
      return (
        <SimpleForm.DropdownSyncTable
          {...baseSearchFormProps}
          dropdownSyncTableProps={{ ...baseFormProps, ...dropdownSyncTableProps }}
        />
      );
    case 'Switch':
      if (baseSearchFormProps.initialValue === undefined) {
        baseSearchFormProps.initialValue = false;
      }
      return (
        <SimpleForm.Switch
          {...baseSearchFormProps}
          switchProps={{ ...baseFormProps, ...switchProps }}
        />
      );
    case 'Checkbox':
      if (baseSearchFormProps.initialValue === undefined) {
        baseSearchFormProps.initialValue = false;
      }
      return (
        <SimpleForm.Checkbox
          {...baseSearchFormProps}
          checkboxProps={{ ...baseFormProps, ...checkboxProps }}
        />
      );
    case 'Cascader':
      let apiTrigger: apiTriggerType[] = [];
      const apiTriggerOut = cascaderProps?.apiTrigger;
      if (apiTriggerOut) {
        if (Array.isArray(apiTriggerOut)) {
          apiTrigger = apiTriggerOut;
        } else {
          apiTrigger = [apiTriggerOut];
        }
      };
      if (baseSearchFormProps.initialValue && !apiTrigger.includes('init')) {
        apiTrigger.push('init');
      }
      if (cascaderProps?.beforeFetch && !apiTrigger.includes('onFocus')) {
        apiTrigger.push('onFocus');
      }
      return (
        <SimpleForm.Cascader
          {...baseSearchFormProps}
          cascaderProps={{
            ...baseFormProps,
            ...cascaderProps,
            apiTrigger: apiTrigger.length ? apiTrigger : undefined,
            beforeFetch: cascaderProps?.beforeFetch ? (params: any) => {
              return cascaderProps.beforeFetch?.(params, (typeof rowIndex === 'number') ? {...record, ...(form?.getFieldValue([GROUP_NAME, rowIndex]) ?? {})} : record, rowIndex);
            } : undefined,
            onChange: (value: any, item: any) => {
              associationFiledName && form?.setFieldValue(associationFiledName, undefined);
              cascaderProps?.onChange?.(value, item, form, rowIndex, (typeof rowIndex === 'number') ? {...record, ...(form?.getFieldValue([GROUP_NAME, rowIndex]) ?? {})} : record);
            },
          }}
        />
      );
    case 'CheckboxGroup':
      return (
        <SimpleForm.CheckboxGroup
          {...baseSearchFormProps}
          enumCode={enumCode}
          checkboxGroupProps={{ ...baseFormProps, ...checkboxGroupProps }}
        />
      );
    case 'DatePicker':
      return (
        <SimpleForm.DatePicker
          {...baseSearchFormProps}
          datePickerProps={{
            ...baseFormProps,
            ...datePickerProps,
          }}
        />
      );
    case 'RangePicker':
      return (
        <SimpleForm.RangePicker
          {...baseSearchFormProps}
          rangePickerProps={{
            ...baseFormProps,
            ...rangePickerProps,
          }}
        />
      );
    case 'TimePicker':
      return (
        <SimpleForm.TimePicker
          {...baseSearchFormProps}
          timePickerProps={{
            ...baseFormProps,
            ...timePickerProps,
          }}
        />
      );
    case 'SuperSelect':
      return (
        <SimpleForm.SuperSelect
          {...baseSearchFormProps}
          enumCode={enumCode}
          superSelectProps={{
            getPopupContainer: () => tableElement || document.body,
            isDisabledEllipsis: true,
            disabledBorderDashed: false,
            ...baseFormProps,
            ...superSelectProps,
            onChange: (value: any, item: any) => {
              associationFiledName && form?.setFieldValue(associationFiledName, undefined);
              superSelectProps?.onChange?.(value, item, form, rowIndex, {...record, ...(form?.getFieldValue([GROUP_NAME, rowIndex]) ?? {})},);
            },
          }}
        />
      );
    case 'SuperSelectTable':
      if (!superSelectTableProps) return null;
      let initOptions_1: any[] = [];
      let labelKey_1 = props?.labelKey;
      let valueKey_1 = props?.valueKey || (Array.isArray(name) ? name[0] : name);
      let label_1 = superSelectTableProps?.labelKey;
      let value_1 = superSelectTableProps?.valueKey;
      if (labelKey_1 && valueKey_1 && label_1 && value_1) {
        const labels = record[labelKey_1];
        const values = record[valueKey_1];
        if (labels !== undefined && values !== undefined) {
          if (Array.isArray(values)) {
            values.forEach((v, k) => {
              initOptions_1.push({
                [label_1 as string]: labels[k],
                [value_1 as string]: v,
              });
            });
          } else {
            initOptions_1.push({
              [label_1]: record[labelKey_1],
              [value_1]: record[valueKey_1],
            });
          }
        }
      }
      const superSelectTableName = getSelectTableNames(baseSearchFormProps.name);
      const labelValue = (Array.isArray(name) && name.length === 2) ? record?.[name[1]] : undefined;
      return (
        <SimpleForm.SuperSelectTable
          {...baseSearchFormProps}
          name={superSelectTableName}
          autoCreateLabelFormItem={false}
          labelValue={labelValue}
          superSelectTableProps={{
            isDisabledEllipsis: true,
            disabledBorderDashed: false,
            initOptions: initOptions_1.length ? initOptions_1 : undefined,
            ...baseFormProps,
            ...superSelectTableProps,
            beforeFetch: superSelectTableProps.beforeFetch ? (params: any) => {
              return superSelectTableProps.beforeFetch?.(params, (typeof rowIndex === 'number') ? {...record, ...(form?.getFieldValue([GROUP_NAME, rowIndex]) ?? {})} : record, rowIndex)
            } : undefined,
            isOpenUpdate: superSelectTableProps.isOpenUpdate || !!superSelectTableProps.beforeFetch,
            onChange: (value: any, item: any) => {
              associationFiledName && form?.setFieldValue(associationFiledName, undefined);
              superSelectTableProps?.onChange?.(value, item, form, rowIndex, (typeof rowIndex === 'number') ? {...record, ...(form?.getFieldValue([GROUP_NAME, rowIndex]) ?? {})} : record);
            },
            getPopupContainer: () => tableElement || document.body,
          }}
        />
      );
    case 'SelectCustom':
      let beforeName: string = '';
      let afterName: string = '';
      if (Array.isArray(name)) {
        beforeName = name[0] as string;
        afterName = name[1] as string;
      }
      return (
        <SimpleForm.SelectCustom
          {...baseSearchFormProps}
          disabled={disabled}
          options={options}
          selectFirstValue={selectFirstValue}
          beforeFormItemFlex={beforeFormItemFlex}
          afterFormItemFlex={afterFormItemFlex}
          middleText={middleText}
          beforeFormItemProps={{
            ...beforeFormItemProps,
            initialValue: getInitialValue(undefined, record, beforeName),
          }}
          afterFormItemProps={{
            ...afterFormItemProps,
            initialValue: getInitialValue(undefined, record, afterName),
          }}
          emptyAfterValue={emptyAfterValue}
          beforeFormItemWidth={beforeFormItemWidth}
        />
      );
    case 'Upload':
      return (
        <SimpleForm.Upload
          {...baseSearchFormProps}
          initialValue={dataToUploads(initialValue, relativePath)}
          uploadProps={{
            size: 'small',
            getPopupContainer: () => tableElement || document.body,
            ...baseFormProps,
            ...uploadProps,
          }}
        />
      );
    case 'UploadImage':
      return (
        <SimpleForm.UploadImage
          className="edit-table-upload-image"
          {...baseSearchFormProps}
          initialValue={dataToUpload(initialValue, relativePath)}
          style={style}
          uploadImageProps={{
            size: 'small',
            getPopupContainer: () => tableElement || document.body,
            ...baseFormProps,
            ...uploadImageProps,
          }}
        />
      );
    case 'UploadImages':
      return (
        <SimpleForm.UploadImages
          className="edit-table-upload-images"
          {...baseSearchFormProps}
          initialValue={dataToUploads(initialValue, relativePath)}
          uploadImagesProps={{
            size: 'small',
            getPopupContainer: () => tableElement || document.body,
            ...baseFormProps,
            ...uploadImagesProps,
          }}
        />
      );
    default:
      return (
        <SimpleForm.Input
          {...baseSearchFormProps}
          inputProps={{ ...baseFormProps, ...inputProps }}
        />
      );
  }
};

export default getFormItem;